'use client';

import { useState, useMemo } from 'react';
import { ChevronUpIcon, ChevronDownIcon, ArrowDownTrayIcon, MagnifyingGlassIcon, XMarkIcon, InformationCircleIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { ESM2to1PredictionItem } from '@/types/esm2to1';
import StockActionIcons from '@/components/stocks/StockActionIcons';
import ReasoningFormatter from '@/components/gsm-stock/ReasoningFormatter';
import ESMStockDetailModal from '@/components/esm-stock/ESMStockDetailModal';

interface ESM2to1PredictionTableProps {
  predictions: ESM2to1PredictionItem[];
}

type SortField = 'stock_name' | 'current_price' | 'market_capitalization' | 'price_to_earning' | 'prediction.exit_date' | 'prediction.days_to_exit';
type SortDirection = 'asc' | 'desc';

export default function ESM2to1PredictionTable({ predictions }: ESM2to1PredictionTableProps) {
  const [sortField, setSortField] = useState<SortField>('prediction.days_to_exit');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPrediction, setSelectedPrediction] = useState<ESM2to1PredictionItem | null>(null);
  const [selectedExitDate, setSelectedExitDate] = useState('');
  const [selectedStock, setSelectedStock] = useState<string | null>(null);

  const handleSort = (field: SortField) => {
    const dir = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(dir);
  };

  const handleStockClick = (isin: string) => {
    setSelectedStock(isin);
  };

  const handleCloseModal = () => {
    setSelectedStock(null);
  };

  // Get unique exit dates for filtering
  const exitDates = useMemo(() => {
    const dates = predictions
      .map(p => p.prediction.exit_date)
      .filter((date, index, arr) => arr.indexOf(date) === index)
      .sort();
    return dates;
  }, [predictions]);

  // Filter and sort predictions
  const filteredAndSortedPredictions = useMemo(() => {
    // Start with all predictions
    let filtered = predictions;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(prediction => 
        (prediction.stock_name && prediction.stock_name.toLowerCase().includes(query)) ||
        (prediction.isin && prediction.isin.toLowerCase().includes(query)) ||
        (prediction.nse_code && prediction.nse_code.toLowerCase().includes(query)) ||
        (prediction.bse_code && prediction.bse_code.toLowerCase().includes(query)) ||
        (prediction.stock_code && prediction.stock_code.toLowerCase().includes(query))
      );
    }

    // Apply exit date filter
    if (selectedExitDate) {
      filtered = filtered.filter(prediction => prediction.prediction.exit_date === selectedExitDate);
    }

    // Apply sorting
    const copy = [...filtered];
    return copy.sort((a, b) => {
      let av: unknown, bv: unknown;
      
      if (sortField === 'prediction.exit_date') {
        av = a.prediction.exit_date;
        bv = b.prediction.exit_date;
      } else if (sortField === 'prediction.days_to_exit') {
        av = a.prediction.days_to_exit;
        bv = b.prediction.days_to_exit;
      } else {
        av = a[sortField as keyof ESM2to1PredictionItem];
        bv = b[sortField as keyof ESM2to1PredictionItem];
      }
      
      const m = sortDirection === 'asc' ? 1 : -1;
      
      if (av == null && bv == null) return 0;
      if (av == null) return 1 * m;
      if (bv == null) return -1 * m;
      
      if (sortField === 'prediction.exit_date') {
        const ad = new Date(av as string).getTime();
        const bd = new Date(bv as string).getTime();
        if (isNaN(ad) && isNaN(bd)) return 0;
        if (isNaN(ad)) return 1 * m;
        if (isNaN(bd)) return -1 * m;
        return (ad - bd) * m;
      }
      
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * m;
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * m;
      return av < bv ? -1 * m : av > bv ? 1 * m : 0;
    });
  }, [predictions, sortField, sortDirection, searchQuery, selectedExitDate]);

  const formatCurrency = (value: number | null | undefined): string => {
    if (typeof value !== 'number' || isNaN(value)) return 'N/A';
    return `₹${value.toFixed(2)}`;
  };

  const formatMarketCap = (value: number | null | undefined): string => {
    if (typeof value !== 'number' || isNaN(value)) return 'N/A';
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const sign = isNegative ? '-' : '';
    
    if (absValue >= 1000) return `${sign}₹${(absValue / 1000).toFixed(2)}K Cr`;
    if (absValue >= 1) return `${sign}₹${absValue.toFixed(2)} Cr`;
    return `${sign}₹${(absValue * 100).toFixed(2)} L`;
  };

  const formatRatio = (value: number | null | undefined): string => {
    if (typeof value !== 'number' || isNaN(value)) return 'N/A';
    return value.toFixed(2);
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getDaysToExitColor = (days: number): string => {
    if (days <= 7) return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    if (days <= 30) return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    if (days <= 90) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  };

  const exportToCSV = () => {
    const headers = [
      'ISIN', 'Stock Name', 'NSE Code', 'BSE Code', 'Stock Code',
      'Current Price', 'Market Cap (Cr)', 'P/E Ratio', 'ESM2 Entry Date',
      'Predicted Exit Date', 'Days to Exit', 'Is Possible', 'TradingView Link', 'Screener Link', 'External Code'
    ];
    
    const csvData = filteredAndSortedPredictions.map(prediction => [
      prediction.isin,
      `"${prediction.stock_name?.replace(/"/g, '""')}"`,
      prediction.nse_code || '',
      prediction.bse_code || '',
      prediction.stock_code || '',
      prediction.current_price || 'N/A',
      prediction.market_capitalization || 'N/A',
      prediction.price_to_earning || 'N/A',
      prediction.esm2_entry_date,
      prediction.prediction.exit_date,
      prediction.prediction.days_to_exit,
      prediction.prediction.is_possible ? 'Yes' : 'No',
      prediction.trading_view_navlink || 'N/A',
      prediction.screener_navlink || 'N/A',
      prediction.stock_code ? `"${prediction.stock_code},"` : ''
    ]);

    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `esm_2to1_predictions_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ChevronUpIcon className="w-4 h-4 text-zinc-400" />;
    }
    return sortDirection === 'asc' ? 
      <ChevronUpIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> : 
      <ChevronDownIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />;
  };

  if (!predictions.length) {
    return (
      <div className="text-center py-12 bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800">
        <p className="text-zinc-500 dark:text-zinc-400 text-lg">No ESM 2-to-1 predictions found</p>
        <p className="text-zinc-400 dark:text-zinc-500 text-sm mt-2">Try adjusting your search criteria</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm overflow-hidden table-container-with-popups">
      {/* Header with Search and Export Button */}
      <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
        <div className="flex flex-col gap-4">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
                ESM 2-to-1 Exit Predictions ({filteredAndSortedPredictions.length}{filteredAndSortedPredictions.length !== predictions.length ? ` of ${predictions.length}` : ''})
              </h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                Predicted exit dates for stocks transitioning from ESM Stage 2 to Stage 1
              </p>
            </div>
            
            <button
              onClick={exportToCSV}
              className="inline-flex items-center px-4 py-2 border border-zinc-300 dark:border-zinc-700 rounded-lg text-sm font-semibold text-zinc-700 dark:text-zinc-200 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Export CSV
            </button>
          </div>

          {/* Filters Row */}
          <div className="flex flex-col gap-4">
            {/* First Row of Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Exit Date Filter */}
              <div className="flex-shrink-0">
                <label className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  Exit Date
                </label>
                <select
                  value={selectedExitDate}
                  onChange={(e) => setSelectedExitDate(e.target.value)}
                  className="block w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                >
                  <option value="">All Dates</option>
                  {exitDates.map(date => (
                    <option key={date} value={date}>
                      {formatDate(date)} ({predictions.filter(p => p.prediction.exit_date === date).length})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Second Row - Search */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search Input */}
              <div className="relative flex-1 max-w-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-zinc-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search stocks, ISIN, codes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-10 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <XMarkIcon className="h-4 w-4 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper-popup-safe min-table-height">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-800">
            <thead className="bg-zinc-50 dark:bg-zinc-800/60">
              <tr>
                <th 
                  onClick={() => handleSort('stock_name')}
                  className="px-4 py-3 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  <div className="flex items-center gap-1">
                    Stock Details
                    <SortIcon field="stock_name" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('market_capitalization')}
                  className="px-4 py-3 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    Market Cap
                    <SortIcon field="market_capitalization" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('price_to_earning')}
                  className="px-4 py-3 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    P/E Ratio
                    <SortIcon field="price_to_earning" />
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                  Prediction Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-zinc-900 divide-y divide-zinc-200 dark:divide-zinc-800">
              {filteredAndSortedPredictions.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <MagnifyingGlassIcon className="h-12 w-12 text-zinc-400 mb-4" />
                      <p className="text-zinc-500 dark:text-zinc-400 text-lg">No predictions match your search</p>
                      <p className="text-zinc-400 dark:text-zinc-500 text-sm mt-2">
                        {searchQuery ? 'Try adjusting your search terms' : 'No predictions available'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAndSortedPredictions.map((prediction) => (
                  <tr 
                    key={prediction.isin} 
                    className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors table-row-with-menu"
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex-1">
                        <StockActionIcons
                          isin={prediction.isin}
                          stockName={prediction.stock_name}
                          tradingViewLink={prediction.trading_view_navlink}
                          screenerLink={prediction.screener_navlink}
                          currentPrice={prediction.current_price || undefined}
                          showNotesCount
                        />
                        <div 
                          className="text-sm font-medium text-zinc-900 dark:text-white cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400"
                          onClick={() => handleStockClick(prediction.isin)}
                        >
                          {prediction.stock_name}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-zinc-900 dark:text-white text-right">
                      {formatMarketCap(prediction.market_capitalization)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-zinc-900 dark:text-white text-right">
                      {formatRatio(prediction.price_to_earning)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <div className="space-y-2">
                        <div className="flex flex-col items-center gap-1">
                          <div className="flex items-center gap-2">
                            <CalendarDaysIcon className="h-4 w-4 text-zinc-400" />
                            <span className="text-sm font-medium text-zinc-900 dark:text-white">
                              {formatDate(prediction.prediction.exit_date)}
                            </span>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDaysToExitColor(prediction.prediction.days_to_exit)}`}>
                            {prediction.prediction.days_to_exit} days
                          </span>
                        </div>
                        <button
                          onClick={() => setSelectedPrediction(prediction)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 rounded-md hover:bg-emerald-500/20 transition-colors"
                          title="View prediction reasoning"
                        >
                          <InformationCircleIcon className="h-3 w-3" />
                          View Details
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 bg-zinc-50 dark:bg-zinc-800/60 border-t border-zinc-200 dark:border-zinc-800">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          Showing {filteredAndSortedPredictions.length} prediction{filteredAndSortedPredictions.length !== 1 ? 's' : ''}
          {selectedExitDate && ` for exit date ${formatDate(selectedExitDate)}`}
        </p>
      </div>

      {/* Reasoning Modal */}
      {selectedPrediction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-xl max-w-5xl w-full max-h-[85vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-zinc-900 dark:text-white">
                    ESM 2-to-1 Exit Prediction Analysis
                  </h3>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                    {selectedPrediction.stock_name} ({selectedPrediction.isin})
                  </p>
                </div>
                <button
                  onClick={() => setSelectedPrediction(null)}
                  className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="px-6 py-6 overflow-y-auto max-h-[calc(85vh-200px)]">
              <div className="space-y-6">
                {/* Prediction Status Overview */}
                <div className="bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-800 p-4 rounded-xl">
                  <h4 className="font-semibold text-zinc-900 dark:text-white mb-3 text-lg">Prediction Summary</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="text-center">
                      <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200">
                        {formatDate(selectedPrediction.prediction.exit_date)}
                      </span>
                      <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">Predicted Exit Date</p>
                    </div>
                    <div className="text-center">
                      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getDaysToExitColor(selectedPrediction.prediction.days_to_exit)}`}>
                        {selectedPrediction.prediction.days_to_exit} Days
                      </span>
                      <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">Days to Exit</p>
                    </div>
                    <div className="text-center">
                      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                        selectedPrediction.prediction.is_possible 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {selectedPrediction.prediction.is_possible ? 'Possible' : 'Not Possible'}
                      </span>
                      <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">Exit Status</p>
                    </div>
                  </div>
                </div>

                {/* Stock Details */}
                <div>
                  <h4 className="font-semibold text-zinc-900 dark:text-white mb-4 text-lg">Stock Information</h4>
                  <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-4">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-white dark:bg-zinc-900 p-3 rounded-lg border border-zinc-200 dark:border-zinc-800">
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">Current Price</div>
                        <div className="text-lg font-semibold text-zinc-900 dark:text-white">{formatCurrency(selectedPrediction.current_price)}</div>
                      </div>
                      <div className="bg-white dark:bg-zinc-900 p-3 rounded-lg border border-zinc-200 dark:border-zinc-800">
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">Market Cap</div>
                        <div className="text-lg font-semibold text-zinc-900 dark:text-white">{formatMarketCap(selectedPrediction.market_capitalization)}</div>
                      </div>
                      <div className="bg-white dark:bg-zinc-900 p-3 rounded-lg border border-zinc-200 dark:border-zinc-800">
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">P/E Ratio</div>
                        <div className="text-lg font-semibold text-zinc-900 dark:text-white">{formatRatio(selectedPrediction.price_to_earning)}</div>
                      </div>
                      <div className="bg-white dark:bg-zinc-900 p-3 rounded-lg border border-zinc-200 dark:border-zinc-800">
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">ESM2 Entry Date</div>
                        <div className="text-lg font-semibold text-zinc-900 dark:text-white">{formatDate(selectedPrediction.esm2_entry_date)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detailed Reasoning */}
                <div>
                  <h4 className="font-semibold text-zinc-900 dark:text-white mb-4 text-lg flex items-center">
                    <InformationCircleIcon className="h-5 w-5 mr-2 text-emerald-600 dark:text-emerald-400" />
                    Detailed Analysis & Reasoning
                  </h4>
                  <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-6">
                    <ReasoningFormatter 
                      text={selectedPrediction.prediction.reasoning}
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/60 flex justify-end">
              <button
                onClick={() => setSelectedPrediction(null)}
                className="px-6 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ESM Stock Detail Modal */}
      {selectedStock && (
        <ESMStockDetailModal 
          isin={selectedStock} 
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
